import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { useTheme, useMediaQuery } from '@mui/material';
import { useState, useRef, useEffect } from 'react';

import Heading from './Heading';

const ModalBox = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    zIndex: theme.zIndex.drawer + 1,
});

const PaperStyled = (theme) => ({
    backgroundColor: 'transparent',
    boxShadow: theme.shadows[5],
    padding: {
        xs: '0px 40px 16px 40px',
        sm: '0px 40px 20px 40px',
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
});

export default function FormModal({
    children,
    title,
    open,
    onClose,
    subText = '',
    badge,
    stickyFooter,
    progressSteps
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!open) setIsScrolled(false);
    }, [open]);

    useEffect(() => {
        if (open) {
            window.helpcrunchModalOpen = true;
            if (window.HelpCrunch) window.HelpCrunch('hideChatWidget');
        } else {
            window.helpcrunchModalOpen = false;
            if (window.updateHelpCrunchVisibility) window.updateHelpCrunchVisibility();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const timer = setTimeout(() => {
            const el = scrollRef.current;
            if (!el) return;

            const handleScroll = () => {
                setIsScrolled(el.scrollTop > 0);
            };

            el.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();

            return () => el.removeEventListener('scroll', handleScroll);
        }, 100);

        return () => clearTimeout(timer);
    }, [open]);

    return (
        <Drawer
            id="form-drawer"
            sx={ModalBox}
            open={open}
            onClose={onClose}
            anchor="right"
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 640, md: 760 },
                    maxWidth: isMobile ? 'calc(100% - 32px)' : '100vw',
                    height: isMobile ? 'calc(100vh - 32px)' : '100vh',
                    ...(isMobile
                        ? {
                              margin: '16px',
                              borderRadius: '20px !important',
                          }
                        : { margin: 0 }),
                },
            }}
        >
            <Box sx={PaperStyled}>

                {/* HEADER */}
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: isScrolled
                            ? 'rgba(245, 241, 237, 0.5)'
                            : 'white',
                        backdropFilter: isScrolled ? 'blur(8px)' : 'none',
                        borderBottom: isScrolled ? '1px solid #e5e7eb' : 'none',
                        zIndex: 5,

                        // ✅ reduced height
                        paddingTop: { xs: '20px', sm: '50px' },
                        paddingBottom: { xs: '8px', sm: '20px' },

                        marginLeft: { xs: '-40px', sm: '-40px' },
                        marginRight: { xs: '-40px', sm: '-40px' },
                        paddingLeft: { xs: '40px', sm: '40px' },
                        paddingRight: { xs: '40px', sm: '40px' },

                        maxHeight: { xs: '35vh', sm: 'none' },
                        overflow: 'hidden',
                    }}
                >
                    <FontAwesomeIcon
                        onClick={onClose}
                        className="cursor-pointer absolute"
                        icon={faXmark}
                        style={{
                            fontSize: '22px',
                            top: '20px',
                            right: '20px',
                        }}
                    />

                    {badge && <Box sx={{ mb: 1 }}>{badge}</Box>}

                    <Heading
                        sx={{
                            fontSize: '32px !important',
                            '@media (max-width: 640px)': {
                                fontSize: '22px !important',
                                lineHeight: '28px'
                            }
                        }}
                    >
                        {title}
                    </Heading>

                    {subText && (
                        <span
                            style={{
                                marginTop: '6px',
                                fontSize: '13px',
                                lineHeight: '18px',
                                color: '#999CA0',

                                // ✅ clamp
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                        >
                            {subText}
                        </span>
                    )}

                    {progressSteps && <Box sx={{ mt: 2 }}>{progressSteps}</Box>}
                </Box>

                {/* CONTENT */}
                <Box
                    ref={scrollRef}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        paddingTop: '16px',
                        paddingBottom: '12px',
                        marginLeft: { xs: '-40px', sm: '-40px' },
                        marginRight: { xs: '-40px', sm: '-40px' },
                        paddingLeft: { xs: '40px', sm: '40px' },
                        paddingRight: { xs: '40px', sm: '40px' },
                    }}
                >
                    {children}
                </Box>

                {/* FOOTER */}
                {stickyFooter && (
                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'white',
                            borderTop: '1px solid #e5e7eb',
                            zIndex: 5,
                        }}
                    >
                        {stickyFooter}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}